//
// EXTERNAL EVENTS
//

/**
 * Fired when debug info changes.
 */
export const EVENT_APP_UPDATED_DEBUG_INFO = "app::updatedDebugInfo";

/**
 * Fired when debug info changes.
 */
export const EVENT_APP_SYSTEM_EXECUTION_PLAN_UPDATED = "app::updatedExecutionPlan";

/**
 * Request the current state of the client app including client metadata and feature switches.
 */
export const EVENT_REQUEST_APP_AVATAR_STATE = "avatar::requestAppState";

/**
 * Event triggered by the host application to to set the initial state.
 * This event replaces `EVENT_APP_FEATURE_SWITCHES` and `EVENT_APP_METADATA`
 * i.e.:
 * `pc.app.fire("app::setInitialState", JSON.stringify({
 *   appMetadata: {},
 *   appFeatureSwitches: [],
 * }))`
 */
export const EVENT_SET_APP_INITIAL_STATE = "app::host::setInitialState";

/**
 * The current list and state of Feature Switches in the host app
 * This event takes a JSON string listing all feature switches with the current value
 * e.g.: `[{"name":"iOS_3DMonsterCustomizerV2","value":"test"}]`
 * @deprecated use EVENT_SET_APP_INITIAL_STATE instead
 */
export const EVENT_APP_FEATURE_SWITCHES = "app::host::featureSwitches";

/**
 * The host app can inject meatdata using this event
 * This event takes a JSON key-value pairs string
 * e.g.: `{"buildNumber":"1121","osVersion":"14.4","deviceID":"39D39CB2-D712-40D2-83CD-5E35786C82A6","deviceModel":"iPhone XS","device":"iPhone","appVersion":"7.28.0","platform":"ios"}`
 * @deprecated use EVENT_SET_APP_INITIAL_STATE instead
 */
export const EVENT_APP_METADATA = "app::host::metadata";

/**
 * Request host app to initiate the given flow
 * * e.g.: `{ name: "upsellNotification", params: { key: "value" } }`
 */
export const EVENT_TRIGGER_HOST_FLOW = "app::host::triggerFlow";

/**
 * This event is triggered by Playcanvas when it is ready to capture user interaction
 * This is an outgoing event and takes no parameters.
 */
export const EVENT_APP_INITIAL_LOADING_COMPLETE = "app::initialLoadingComplete";

/**
 * Sets the current state of the avatar using an avatar configuration argument
 * eg. `{ "eyes": "104", "body":"103", "mouth":"005", "feet":"019", "color":"008" }`
 */
export const EVENT_SET_AVATAR_CONFIGURATION = "avatar::setAvatarConfiguration";

/**
 * Called after the avatar configuration is set and all parts are updated.
 */
export const EVENT_DID_SET_AVATAR_CONFIGURATION = "avatar::didSetAvatarConfiguration";

/**
 * Triggered when avatar changes have been saved
 * This is an outgoing event and takes one parameter with the body part saved as string
 * and the avatarURL that is returned from API
 * e.g.: `{ "eyeId": "104", "bodyId":"103", "mouthId":"005", "paintId":"019", "colorId":"008", avatarURL: "//pstudent.classdojo.com/......" }`
 */
export const EVENT_AVATAR_UPDATED = "avatar::monsterAvatarUpdated";

/**
 * Request the application to close 3D Monster World and go back.
 * This is an outgoing event and takes no parameters.
 */
export const EVENT_REQUEST_EXIT_MONSTER_WORLD = "app::requestExitMonsterWorld";

/**
 * This event is triggered to signal loading progress to the launcher.
 */
export const EVENT_APP_SET_LOADING_PROGRESS = "app::setLoadingProgress";

/**
 * This event is triggered when we are done loading and want to hide the loading screen.
 */
export const EVENT_APP_LOADING_COMPLETE = "app::loadingComplete";

/**
 * This event is triggered when the experimental splash screen is ready to be shown.
 */
export const EVENT_APP_EXPERIMENTAL_SPLASH_SCREEN_READY = "app::experimentalSplashScreenReady";

/**
 * This event is triggered when the play button is clicked in the experimental splash screen.
 */
export const EVENT_APP_EXPERIMENTAL_SPLASH_PLAY_CLICKED = "app::experimentalSplashPlayClicked";

/**
 * This event is triggered when the input type changes.
 */
export const EVENT_INPUT_TYPE_CHANGED = "app::inputTypeChanged";

/**
 * This event is triggered when the title animation is done and we can show additional UI
 */
export const EVENT_ISLAND_NAME_ANIMATION_COMPLETE = "app::islandNameAnimationComplete";

/**
 * This event is triggered when we need to show a fatal error message and want to hide the loading screen.
 */
export const EVENT_APP_FATAL_ERROR = "app::fatalError";

/**
 * This event is triggered when we need to update game diagnostic data for the host app.
 */
export const EVENT_APP_GAME_DIAGNOSTICS = "app::gameDiagnostics";
export type OnAppGameDiagnosticsData = {
  /** The timestamp of the diagnostic data in UTC */
  timestamp: number;
  [key: string]: unknown;
};

/**
 * Requests the host app to reload and re-initialize the app web view as soon as possible,
 * so we get the latest monsterworld release from server.
 * This is an outgoing event and takes no parameters.
 */
export const EVENT_REQUEST_APP_RELOAD = "app::requestReload";

/**
 * Event triggered in IFrameBridge whenever we call `console.error()`
 */
export const EVENT_CONSOLE_ERROR = "app::console::error";

/**
 * For the editor sending an override for the assets to load and feature switches.
 */
export const EVENT_EDITOR_DATA = "editor:data";

/**
 * For the launcher requesting the editor data to load.
 */
export const EVENT_REQUEST_EDITOR_DATA = "editor:requestData";

//
// INTERNAL EVENTS
//

/**
 */
export const EVENT_SHOW_ERROR_SCREEN = "app::error::show";

/**
 */
export const EVENT_HIDE_ERROR_SCREEN = "app::error::hide";

/**
 * Event triggered when we are terminating current monsterverse app and navigating to another one
 * It fires with a parm `{ exitingMonsterverse: boolean }` that indicates if we are exiting monsterverse
 * and going back to host app
 */
export const EVENT_APP_TERMINATING = "app::terminating";

/**
 * Data sent when invoking `EVENT_APP_TERMINATING`.
 * @param exitingMonsterverse Set to true when exiting the monsterverse. False if not.
 * @param customizer Set to true if we are entering the Monster Customizer. False if not.
 */
export type OnAppTerminatingData = { exitingMonsterverse?: boolean; customizer?: boolean };

/**
 * Send this event to the app to request logging of systems timing.
 */
export const EVENT_APP_SHOULD_LOG_SYSTEMS_TIMING = "app::shouldLogSystemsTiming";

/**
 * Sent by the app with information about the systems timing.
 */
export const EVENT_APP_SYSTEMS_TIMING = "app::systemsTiming";

/**
 * Sent when all system scheduled have been added
 */
export const EVENT_APP_SYSTEMS_ADDED = "app::systemsAdded";

/**
 * Sent by the app when the active camera has been set.
 */
export const EVENT_DID_SET_ACTIVE_CAMERA = "app::didSetActiveCamera";

/**
 * Fired when materials need to be updated
 */
export const EVENT_MATERIALS_UPDATE = "app:materials:update";

/**
 * Fired when materials need to be updated
 */
export const EVENT_SKYBOX_COLOR_UPDATE = "app:skybox:color:update";

/**
 * Sent by the app with information about the loading times.
 */
export const EVENT_APP_LOG_LOADING_TIME = "app::logLoadingTime";

/**
 * Fired after a mesh-material pair is added to the instancing system.
 */
export const EVENT_BLOCK_MATERIAL_ADDED = "mat:addBlockMaterial";

/**
 * Fire to change the portal transition amount
 */
export const EVENT_SET_PORTAL_TRANSITION_PROGRESS = "app::setPortalTransitionProgress";

/**
 * Fired when dynamic resolution perfLevel changes
 */
export const EVENT_DYNAMIC_RESOLUTION_PERF_LEVEL_CHANGED = "app::dynamicRezPerfLevelChanged";

/**
 * Fired when the camera far clip is restricted
 */
export const EVENT_RESTRICT_CAMERA_FAR_CLIP = "app::restrictCameraFarClip";

/**
 * Fired when the launcher requests the game to terminate and cleanup resources.
 * Must reload after the game responds with EVENT_GAME_HARD_TERMINATED.
 */
export const EVENT_REQUEST_HARD_TERMINATE_GAME = "app:hardTerminateGame";

/**
 * Fired when the game has terminated and cleanup resources. Must reload after this event.
 */
export const EVENT_GAME_HARD_TERMINATED = "app:gameHardTerminated";
